import React from 'react';
import { Popup } from 'semantic-ui-react';
import { Organization, OrganizationListObj, SpotInterface } from 'types';
import logo from 'assets/round.png';
import styles from './DashboardContent.module.css';
import LocationIcon from 'components/Icons/LocationIcon';
import BriefcaseIcon from 'components/Icons/BriefcaseIcon';
import SpotOrganizationSelector from 'components/SpotOrganizationSelector/SpotOrganizationSelector';

type OverviewContentProps = {
    organization: Organization;
    currentSpot: SpotInterface | null;
    orgSpots: SpotInterface[] | null;
    isDemoMode: boolean;
    onSelectSpot: (spot: SpotInterface) => void;
    onSelectOrganization?: (organization: OrganizationListObj) => void;
    children: React.ReactNode;
};

// OverviewContent is meant to be used in the Spot Detail Container *Overview page*.
// All other pages should use DashboardContent component instead.
function OverviewContent({
    orgSpots,
    currentSpot,
    organization,
    isDemoMode,
    onSelectSpot,
    onSelectOrganization,
    children,
}: OverviewContentProps): JSX.Element {
    if (orgSpots === null || orgSpots.length === 0 || currentSpot === null) return <></>;

    const handleSpotSelect = (spot: SpotInterface) => {
        onSelectSpot(spot);
    };

    const handleOrganizationSelect = (org: OrganizationListObj) => {
        if (onSelectOrganization) {
            onSelectOrganization(org);
        }
    };

    const subHeaderLabelClassName = `${styles.subHeaderLabel} ${isDemoMode ? ' blur' : ''}`;

    const subHeader = (
        <div className={styles.subHeaderWrapper}>
            <div className={styles.subHeader}>
                <LocationIcon />
                <span className={subHeaderLabelClassName}>{currentSpot.name}</span>
            </div>
            <div className={styles.subHeader}>
                <BriefcaseIcon />
                <span className={subHeaderLabelClassName}>{organization.name}</span>
            </div>
            {currentSpot.webflow_page_url && (
                <Popup
                    trigger={
                        <a
                            href={currentSpot.webflow_page_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.publicPageLink}
                        >
                            <img src={logo} alt="Logo" height="inherit" />
                        </a>
                    }
                    content="View your public listing"
                    on="hover"
                    inverted
                />
            )}
        </div>
    );

    return (
        <div className={styles.contentView}>
            <div className={styles.overviewHeader}>
                <div className={styles.leftBox}>
                    <div className={styles.selectorWrapper}>
                        <SpotOrganizationSelector
                            currentOrganization={organization}
                            currentSpot={currentSpot}
                            spots={orgSpots}
                            onSelectOrganization={handleOrganizationSelect}
                            onSelectSpot={handleSpotSelect}
                            isDemoMode={isDemoMode}
                        />
                    </div>
                    <div className={styles.smallerScreenSubheader}>{subHeader}</div>
                </div>

                <div className={styles.biggerScreenSubheader}>{subHeader}</div>
            </div>
            <div className={styles.body}>{children}</div>
        </div>
    );
}

export default OverviewContent;
